import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useToast } from '../../hooks/useToast'

// Sales Watch: the weekday morning director digest. The worker
// (companies-house-watch repo, ceo_digest.py) gathers and sends it; this app
// previews what went out and edits the org's sw_config settings.
// Access follows CH Watch org membership (chw_org_members + RLS).

const FONT = 'Lexend Deca, sans-serif'

interface SwOrg { id: string; org_key: string; display_name: string }
interface SwSettings {
  enabled: boolean
  send_at: string
  recipients: string[]
  sections: { tasks: boolean; numbers: boolean; attention: boolean; team: boolean }
  // Optional extra section, configured per organisation. Only orgs that have
  // one see its toggle; it never appears for anyone else.
  owner_book?: { enabled: boolean; owner_id?: string; label?: string }
  [key: string]: unknown // identity fields (portal_key, team, ...) pass through untouched
}
interface SwDigest { digest_date: string; subject: string; html: string; emailed: boolean }

const EDITABLE_DEFAULTS: SwSettings = {
  enabled: true,
  send_at: '06:30',
  recipients: [],
  sections: { tasks: true, numbers: true, attention: true, team: true },
}
const SEND_TIMES = ['05:30', '06:00', '06:30', '07:00', '07:30', '08:00']
const SECTION_LABELS: Array<[keyof SwSettings['sections'], string, string]> = [
  ['tasks', 'Your tasks', 'Open and overdue tasks, oldest first, with links to complete them'],
  ['numbers', 'Yesterday in numbers', 'Enquiries and sources, deals created and moved, month so far'],
  ['attention', 'Needs attention', 'Deals stuck or past their close date, ranked by value'],
  ['team', 'Team activity', 'Calls, emails, deals moved and overdue tasks per rep'],
]

const card: React.CSSProperties = {
  background: 'white', border: '1px solid #e5e7eb', borderRadius: 12,
  boxShadow: '0 1px 2px rgba(0,0,0,0.05)', overflow: 'hidden',
}
const cardHead: React.CSSProperties = {
  padding: '12px 16px', borderBottom: '1px solid #e5e7eb', fontWeight: 600,
  fontSize: 13, color: '#111827', fontFamily: FONT,
  display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
}
const btn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
  background: '#2563eb', color: 'white', fontWeight: 600, borderRadius: 8,
  border: 'none', cursor: 'pointer', fontSize: 13, fontFamily: FONT,
}
const btnQuiet: React.CSSProperties = {
  ...btn, background: 'white', color: '#374151', border: '1px solid #d1d5db', fontWeight: 500,
}
const inputStyle: React.CSSProperties = {
  padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
  fontSize: 13, fontFamily: FONT, background: 'white', outline: 'none',
  boxSizing: 'border-box',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280',
  margin: '0 0 6px', fontFamily: FONT,
}

const Switch: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string }> =
  ({ checked, onChange, label }) => (
    <label style={{ position: 'relative', display: 'inline-block', cursor: 'pointer', flex: 'none' }}>
      <input
        type="checkbox" checked={checked} aria-label={label}
        onChange={e => onChange(e.target.checked)}
        style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', margin: 0, cursor: 'pointer' }}
      />
      <span style={{
        display: 'block', width: 36, height: 20, borderRadius: 999,
        background: checked ? '#2563eb' : '#d1d5db', transition: 'background .15s',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: checked ? 18 : 2, width: 16, height: 16,
          borderRadius: '50%', background: 'white', boxShadow: '0 1px 2px rgba(0,0,0,.25)',
          transition: 'left .15s',
        }} />
      </span>
    </label>
  )

function mergeSettings(stored: Record<string, unknown> | null): SwSettings {
  const d = EDITABLE_DEFAULTS
  const s = stored || {}
  // Stored values win; defaults only fill in what the org has not set. Every
  // other stored key (portal_key, team, owner_book, ...) passes through as is.
  return {
    ...s,
    enabled: (s.enabled as boolean) ?? d.enabled,
    send_at: (s.send_at as string) || d.send_at,
    recipients: (s.recipients as string[]) || d.recipients,
    sections: { ...d.sections, ...((s.sections as SwSettings['sections']) || {}) },
  }
}

const SalesWatchApp: React.FC = () => {
  const { showToast } = useToast()
  const [orgs, setOrgs] = useState<SwOrg[]>([])
  const [orgId, setOrgId] = useState('')
  const [settings, setSettings] = useState<SwSettings | null>(null)
  const [digest, setDigest] = useState<SwDigest | null>(null)
  const [view, setView] = useState<'preview' | 'settings'>('preview')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const org = useMemo(() => orgs.find(o => o.id === orgId) || null, [orgs, orgId])

  useEffect(() => {
    (async () => {
      // orgs the user belongs to that have a Sales Watch config (both RLS-gated)
      const { data: cfgRows, error } = await supabase
        .from('sw_config').select('org_id, settings')
      if (error) { setLoading(false); return }
      const ids = (cfgRows || []).map(r => r.org_id)
      if (!ids.length) { setLoading(false); return }
      const { data: orgRows } = await supabase
        .from('chw_orgs').select('id, org_key, display_name').in('id', ids)
      setOrgs((orgRows || []) as SwOrg[])
      setOrgId((orgRows || [])[0]?.id || '')
      setLoading(false)
    })()
  }, [])

  const loadOrg = useCallback(async (id: string) => {
    setSettings(null)
    setDigest(null)
    const { data: cfg } = await supabase
      .from('sw_config').select('settings').eq('org_id', id).single()
    setSettings(mergeSettings((cfg?.settings as Record<string, unknown>) || null))
    const { data: dg } = await supabase
      .from('sw_digests').select('digest_date, subject, html, emailed')
      .eq('org_id', id).order('digest_date', { ascending: false }).limit(1)
    setDigest(((dg || [])[0] as SwDigest) || null)
  }, [])

  useEffect(() => { if (orgId) loadOrg(orgId) }, [orgId, loadOrg])

  const save = async () => {
    if (!settings || !orgId) return
    setSaving(true)
    try {
      const { error } = await supabase
        .from('sw_config').update({ settings }).eq('org_id', orgId)
      if (error) throw error
      showToast('success', 'Saved. Applies from the next digest')
    } catch {
      showToast('error', 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div style={{ padding: 40, fontFamily: FONT, color: '#6b7280' }}>Loading…</div>
  }
  if (!org) {
    return (
      <div style={{ padding: 40, fontFamily: FONT, maxWidth: 560 }}>
        <h2 style={{ fontSize: 18, color: '#111827', marginBottom: 8 }}>Sales Watch</h2>
        <p style={{ color: '#6b7280', fontSize: 14 }}>
          Sales Watch is not set up for your organisation yet.
          Ask your administrator.
        </p>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh', padding: '24px 28px', fontFamily: FONT,
      background: 'linear-gradient(to bottom right,#f1f5f9,#dbeafe,#e0e7ff)',
    }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Sales Watch</h1>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              {org.display_name} · every weekday at {settings?.send_at || '06:30'}
              {settings && !settings.enabled ? ' · paused' : ''}
            </div>
          </div>
          {orgs.length > 1 && (
            <select value={orgId} onChange={e => setOrgId(e.target.value)}
              style={{ ...inputStyle, width: 200 }} aria-label="Organisation">
              {orgs.map(o => <option key={o.id} value={o.id}>{o.display_name}</option>)}
            </select>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button style={view === 'preview' ? btn : btnQuiet} onClick={() => setView('preview')}>
              Latest digest
            </button>
            <button style={view === 'settings' ? btn : btnQuiet} onClick={() => setView('settings')}>
              Settings
            </button>
          </div>
        </div>

        {view === 'preview' && (
          <div style={card}>
            <div style={cardHead}>
              {digest
                ? <>
                    <span>{digest.subject || 'Digest'}</span>
                    <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>
                      {digest.digest_date}{digest.emailed ? ' · emailed' : ' · not emailed'}
                    </span>
                  </>
                : 'Latest digest'}
            </div>
            {digest
              ? <iframe title="digest" srcDoc={digest.html} sandbox=""
                  style={{ border: 'none', width: '100%', minHeight: '75vh', background: 'white' }} />
              : <div style={{ padding: 20, color: '#6b7280', fontSize: 13 }}>
                  No digest sent yet. The first one goes out on the next weekday
                  morning at {settings?.send_at || '06:30'}, and a copy will appear here.
                </div>}
          </div>
        )}

        {view === 'settings' && settings && (
          <div style={{ display: 'grid', gap: 18, maxWidth: 640 }}>
            <div style={card}>
              <div style={cardHead}>
                <span>Delivery</span>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: '#6b7280' }}>
                  {settings.enabled ? 'On' : 'Paused'}
                  <Switch checked={settings.enabled} label="Digest on or off"
                    onChange={v => setSettings({ ...settings, enabled: v })} />
                </span>
              </div>
              <div style={{ padding: '14px 18px', opacity: settings.enabled ? 1 : 0.5 }}>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <label style={labelStyle}>Send at (UK time, weekdays)</label>
                    <select value={settings.send_at} style={{ ...inputStyle, width: 110 }}
                      onChange={e => setSettings({ ...settings, send_at: e.target.value })}>
                      {SEND_TIMES.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <label style={labelStyle}>Send to (comma separated)</label>
                    <input
                      type="text" value={settings.recipients.join(', ')}
                      style={{ ...inputStyle, width: '100%' }}
                      placeholder="guy@yourcompany.co.uk"
                      onChange={e => setSettings({
                        ...settings,
                        recipients: e.target.value.split(',').map(x => x.trim()).filter(Boolean),
                      })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div style={card}>
              <div style={cardHead}>
                <span>Sections</span>
                <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>
                  same order every day, so you learn where to look
                </span>
              </div>
              <div style={{ padding: '10px 12px' }}>
                {SECTION_LABELS.map(([key, label, sub]) => (
                  <label key={key} style={{
                    display: 'flex', gap: 10, alignItems: 'baseline',
                    padding: '8px 8px', borderRadius: 7, cursor: 'pointer',
                  }}>
                    <input
                      type="checkbox" checked={settings.sections[key]}
                      onChange={e => setSettings({
                        ...settings,
                        sections: { ...settings.sections, [key]: e.target.checked },
                      })}
                      style={{ accentColor: '#2563eb' }}
                    />
                    <span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{label}</span><br />
                      <span style={{ fontSize: 11.5, color: '#6b7280' }}>{sub}</span>
                    </span>
                  </label>
                ))}
                {settings.owner_book?.owner_id && (
                  <label style={{
                    display: 'flex', gap: 10, alignItems: 'baseline',
                    padding: '8px 8px', borderRadius: 7, cursor: 'pointer',
                    borderTop: '1px solid #f3f4f6', marginTop: 4,
                  }}>
                    <input
                      type="checkbox" checked={settings.owner_book.enabled}
                      onChange={e => setSettings({
                        ...settings,
                        owner_book: { ...settings.owner_book, enabled: e.target.checked },
                      })}
                      style={{ accentColor: '#2563eb' }}
                    />
                    <span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                        {settings.owner_book.label || 'Reassignments'} <span style={{
                          fontSize: 10, fontWeight: 700, letterSpacing: '.05em',
                          textTransform: 'uppercase', color: '#92400e',
                          background: '#fef3c7', borderRadius: 4, padding: '1px 6px',
                          marginLeft: 6,
                        }}>Temporary</span>
                      </span><br />
                      <span style={{ fontSize: 11.5, color: '#6b7280' }}>
                        Companies still waiting to be reassigned. Switch off when
                        the redistribution is finished.
                      </span>
                    </span>
                  </label>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button onClick={save} disabled={saving} style={{ ...btn, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save settings'}
              </button>
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                Applies from the next digest.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SalesWatchApp
